/**
 * @module components/AmountPicker
 * @description Preset donation amounts + custom whole-dollar field (brief §4.5).
 * Controlled; shared by Quick View and the guided checkout.
 */
import { useId, type JSX } from "react";
import { GIFT_PRESETS } from "../config/page";
import { MIN_GIFT_USD } from "../domain/cart";
import { formatUsd } from "../lib/format";
import "./AmountPicker.css";

/** Props for <AmountPicker>. */
interface AmountPickerProps {
    value: number;
    onChange: (amountUsd: number) => void;
    label?: string;
}

/** Preset buttons plus a custom amount input. */
export function AmountPicker({ value, onChange, label = "Choose an amount" }: AmountPickerProps): JSX.Element {
    const customId: string = useId();
    const isPreset: boolean = GIFT_PRESETS.includes(value);
    return (
        <fieldset className="amount-picker">
            <legend className="amount-picker__legend">{label}</legend>
            <div className="amount-picker__presets">
                {GIFT_PRESETS.map((amount: number) => (
                    <button key={amount} type="button" className="amount-picker__preset" aria-pressed={value === amount} onClick={() => onChange(amount)}>
                        {formatUsd(amount)}
                    </button>
                ))}
            </div>
            <label className="amount-picker__custom" htmlFor={customId}>
                <span>Other amount</span>
                <span className="amount-picker__input">
                    <span aria-hidden="true">$</span>
                    <input id={customId} type="number" inputMode="numeric" min={MIN_GIFT_USD} step={1}
                        value={isPreset ? "" : String(value)} placeholder="Enter amount"
                        onChange={(e) => {
                            const n: number = Number.parseInt(e.target.value, 10);
                            if (Number.isFinite(n) && n >= MIN_GIFT_USD) onChange(n);
                        }} />
                </span>
            </label>
        </fieldset>
    );
}
